import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router";
import { isFeatureEnabled } from "@shared-core/config/features";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { CustomerListOptions } from "@shared-core/data/adapter";
import type * as API from "@shared-core/data/api-types";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { fetchCustomerSuggestions } from "@shared-core/domain/searchSuggestions";
import { formatCount } from "@shared-core/domain/formatters";
import {
  isScopeOverrideRole,
  useScopeOverrideValue,
} from "@shared-core/domain/scopeOverride";
import { useAuth, useCanEditCustomers } from "../app/auth/hooks";
import { Button } from "../app/components/primitives/button";
import { SearchTypeahead } from "../app/components/patterns/SearchTypeahead";
import { CustomerCard } from "../app/components/domain/customers/CustomerCard";
import { CustomerStrategyRowCard } from "../app/components/domain/customers/CustomerStrategyRowCard";
import {
  COVERAGE_KEYS,
  COVERAGE_LABEL,
  HEALTH_TIER_KEYS,
  HEALTH_TIER_LABEL,
  UNTAPPED_TIER_KEYS,
  UNTAPPED_TIER_LABEL,
  type CoverageKey,
  type HealthTierKey,
  type UntappedTierKey,
} from "../app/components/domain/customers/strategyTiers";
import { TicketModal } from "../app/components/domain/tickets/TicketModal";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { ArrowUpDown } from "lucide-react";
import { paths } from "@shared-core/routing/routes";

// CustomersPage follows the canonical list-endpoint pattern (see
// docs/list-endpoints.md): the backend does filter/sort/search/paginate.
// Segment tab + search + sort + page are component state synced to URL;
// a single useEffect refetches on change. No client-side .filter()/.sort().
//
// The "Pulse | Strategy" view toggle is a pure UI concern: the backend
// always returns both pulse and strategy fields on every row. Toggling
// changes which row component renders and which filter pills and sort
// options are exposed.

type ViewMode = "pulse" | "strategy";

type PulseSortOption =
  | "revenue_12mo"
  | "revenue_90d"
  | "estimates_total"
  | "jobs_total"
  | "last_estimate"
  | "last_job"
  | "alerts_count"
  | "tickets_count";

type StrategySortOption =
  | "health_score_asc"
  | "untapped_gbp_desc"
  | "wallet_gbp_desc"
  | "strategic_key_contacts_asc"
  | "name";

// UI sort option -> backend sort param (see action whitelist).
const PULSE_SORT_TO_PARAM: Record<PulseSortOption, string> = {
  revenue_12mo: "-revenue_last_12_months",
  revenue_90d: "-revenue_last_90_days",
  estimates_total: "-estimates_total_amount",
  jobs_total: "-live_jobs_total_amount",
  last_estimate: "-last_estimate_date",
  last_job: "-last_order_date",
  alerts_count: "-open_alerts_count",
  tickets_count: "-open_tickets_count",
};

const STRATEGY_SORT_TO_PARAM: Record<StrategySortOption, string> = {
  // Default: worst-first health surfaces at-risk accounts.
  health_score_asc: "health_score",
  untapped_gbp_desc: "-untapped_gbp",
  wallet_gbp_desc: "-wallet_gbp",
  strategic_key_contacts_asc: "strategic_key_contacts_count",
  name: "name",
};

const PULSE_DEFAULT_SORT: PulseSortOption = "revenue_12mo";
const STRATEGY_DEFAULT_SORT: StrategySortOption = "health_score_asc";

const PAGE_SIZE = 25;

function parseView(raw: string | null): ViewMode {
  return raw === "strategy" ? "strategy" : "pulse";
}

function parseCsv(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
}

export function CustomersPage() {
  const dataAdapter = useDataAdapter();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { showAll, setShowAll } = useScopeOverrideValue({ role: user?.role });
  const canToggleScope = isScopeOverrideRole(user?.role);
  // "New customer" (v1 completion C5): customers:edit, and the page is live in
  // production before the create is.
  const canCreateCustomer =
    useCanEditCustomers() && isFeatureEnabled("v1_completion");

  const view: ViewMode = parseView(searchParams.get("view"));

  // Colleague free-look — pick a specific sales_rep/csr's portfolio instead
  // of "my customers" or "all customers". "" means "my customers" (the
  // default, same as today). URL-synced like the page's other filters, so
  // "show me Dana's customers" is a shareable link.
  const viewedUserId = searchParams.get("viewed_user_id") ?? "";
  const setViewedUserId = useCallback(
    (next: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next) {
            params.set("viewed_user_id", next);
          } else {
            params.delete("viewed_user_id");
          }
          params.set("page", "1");
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [colleagues, setColleagues] = useState<{ id: string; name: string }[]>(
    [],
  );

  // Populate the colleague dropdown once, for scope-override-eligible
  // roles only. Reads the staff list (GET /users) — the app's one
  // "who can I pick from" list — narrowed to active sales_rep/csr via
  // the `roles` filter so this doesn't also list admins/managers/CSRs'
  // managers etc.
  useEffect(() => {
    if (!canToggleScope) return;
    let cancelled = false;
    dataAdapter
      .listUsers({ roles: ["sales_rep", "csr"] })
      .then((page) => {
        if (!cancelled) {
          setColleagues(page.items.map((u) => ({ id: u.id, name: u.name })));
        }
      })
      .catch((error) => {
        console.error("Error loading colleagues:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, canToggleScope]);

  const [customers, setCustomers] = useState<
    API.CustomerIntelligenceListItem[]
  >([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  // `firstLoad` blocks initial render only; subsequent refetches keep the
  // page mounted so the search input never loses focus.
  const [firstLoad, setFirstLoad] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  // Typeahead suggestions: customers only. Picking one opens that customer;
  // the text itself still drives the live list filter.
  const fetchSuggestions = useCallback(
    (q: string) => fetchCustomerSuggestions(dataAdapter, q),
    [dataAdapter],
  );

  const [segmentFilter, setSegmentFilter] = useState<string>("all");
  const [pulseSort, setPulseSort] =
    useState<PulseSortOption>(PULSE_DEFAULT_SORT);
  const [strategySort, setStrategySort] = useState<StrategySortOption>(
    STRATEGY_DEFAULT_SORT,
  );
  const [ticketModalCustomerId, setTicketModalCustomerId] = useState<
    string | null
  >(null);
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
  const [segmentCounts, setSegmentCounts] = useState({
    all: 0,
    strategic: 0,
    key: 0,
    client: 0,
  });

  // Strategy-view filter pills. URL-synced as comma-joined values.
  const coverageFilters = useMemo(
    () => parseCsv(searchParams.get("coverage")) as CoverageKey[],
    [searchParams],
  );
  const healthTierFilters = useMemo(
    () => parseCsv(searchParams.get("health_tier")) as HealthTierKey[],
    [searchParams],
  );
  const untappedTierFilters = useMemo(
    () => parseCsv(searchParams.get("untapped_tier")) as UntappedTierKey[],
    [searchParams],
  );

  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);

  const setPage = useCallback(
    (p: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("page", String(p));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setView = useCallback(
    (next: ViewMode) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === "strategy") {
            params.set("view", "strategy");
          } else {
            params.delete("view");
            // Strategy-only filters don't apply to pulse — clear to avoid
            // silently-filtered lists when the user flips back.
            params.delete("coverage");
            params.delete("health_tier");
            params.delete("untapped_tier");
          }
          params.set("page", "1");
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const toggleStrategyFilter = useCallback(
    (key: "coverage" | "health_tier" | "untapped_tier", value: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          const current = parseCsv(params.get(key));
          const nextSet = new Set(current);
          if (nextSet.has(value)) {
            nextSet.delete(value);
          } else {
            nextSet.add(value);
          }
          if (nextSet.size === 0) {
            params.delete(key);
          } else {
            params.set(key, Array.from(nextSet).join(","));
          }
          params.set("page", "1");
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Reset to page 1 whenever filters/sort/search change.
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    segmentFilter,
    debouncedSearch,
    pulseSort,
    strategySort,
    view,
    showAll,
    viewedUserId,
  ]);

  // Refetch whenever filter/page state changes. Search is applied on the
  // server so there is no client-side filter or sort on the response.
  // Segment-tab counts come back on the same response (segmentCounts),
  // honouring every filter except segment_id — so a single round-trip
  // refreshes both the items list and the tab counts.
  useEffect(() => {
    let cancelled = false;
    async function loadCustomers() {
      const sort =
        view === "strategy"
          ? STRATEGY_SORT_TO_PARAM[strategySort]
          : PULSE_SORT_TO_PARAM[pulseSort];
      const itemsOptions: CustomerListOptions = {
        sort,
        page,
        pageSize: PAGE_SIZE,
      };
      if (debouncedSearch.trim()) itemsOptions.q = debouncedSearch.trim();
      if (view === "strategy") {
        if (coverageFilters.length > 0) itemsOptions.coverage = coverageFilters;
        if (healthTierFilters.length > 0)
          itemsOptions.healthTier = healthTierFilters;
        if (untappedTierFilters.length > 0)
          itemsOptions.untappedTier = untappedTierFilters;
      }
      if (segmentFilter !== "all") itemsOptions.segmentId = segmentFilter;
      // Mutually exclusive with "show all" (X-Scope-Override header) — the
      // UI only ever has one active at a time, but guard here too rather
      // than relying solely on that.
      if (viewedUserId && !showAll) itemsOptions.viewedUserId = viewedUserId;
      try {
        const itemsResp = await dataAdapter.getCustomers(itemsOptions);
        if (cancelled) return;
        setCustomers(itemsResp.items);
        setTotal(itemsResp.total);
        setTotalPages(itemsResp.totalPages);
        const facets = itemsResp.segmentCounts ?? {};
        const strategic = facets.strategic ?? 0;
        const key = facets.key ?? 0;
        const client = facets.client ?? 0;
        setSegmentCounts({
          all: strategic + key + client,
          strategic,
          key,
          client,
        });
      } catch (error) {
        console.error("Error loading customers:", error);
      } finally {
        if (!cancelled) setFirstLoad(false);
      }
    }
    loadCustomers();
    return () => {
      cancelled = true;
    };
  }, [
    dataAdapter,
    segmentFilter,
    debouncedSearch,
    pulseSort,
    strategySort,
    view,
    page,
    coverageFilters,
    healthTierFilters,
    untappedTierFilters,
    showAll,
    viewedUserId,
  ]);

  const handleAddTicket = async (data: {
    title: string;
    description: string;
    actionType: "call" | "email" | "meeting" | "general";
    dueDate: string;
    priority: "low" | "normal" | "high" | "urgent";
    categoryId: string | null;
    customerId: string | null;
    assignedToUserId: string | null;
    customerPersonId: string | null;
    subjectType: "alert" | "opportunity" | "job" | "estimate" | null;
    subjectId: string | null;
    subjectLabel: string | null;
  }) => {
    if (!ticketModalCustomerId) return;
    setIsSubmittingTicket(true);
    try {
      await dataAdapter.createTicket({
        title: data.title,
        description: data.description || undefined,
        due_date: data.dueDate || undefined,
        action_type: data.actionType,
        customer_id: data.customerId ?? ticketModalCustomerId,
        priority: data.priority,
        category_id: data.categoryId ?? undefined,
        assigned_to_user_id: data.assignedToUserId ?? undefined,
        customer_person_id: data.customerPersonId ?? undefined,
        subject_type: data.subjectType ?? undefined,
        subject_id: data.subjectId ?? undefined,
        subject_label: data.subjectLabel ?? undefined,
      });
      setTicketModalCustomerId(null);
    } catch (error) {
      console.error("Error creating ticket:", error);
    } finally {
      setIsSubmittingTicket(false);
    }
  };

  const segments = [
    { id: "all", label: "All", count: segmentCounts.all },
    { id: "strategic", label: "Strategic", count: segmentCounts.strategic },
    { id: "key", label: "Key", count: segmentCounts.key },
    { id: "client", label: "Client", count: segmentCounts.client },
  ];

  if (firstLoad) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Loading customers...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
          <h1 className="text-2xl">Customers</h1>
          <div className="flex flex-wrap items-center gap-2">
            {canCreateCustomer && (
              <Button asChild>
                <Link to={paths.customerCreate()}>New customer</Link>
              </Button>
            )}
            {/* View toggle — pulse vs strategy. Purely a frontend
              concern; the backend always returns both shapes. */}
            <div
              role="tablist"
              aria-label="View"
              className="inline-flex rounded-md border border-border overflow-hidden"
            >
              <button
                type="button"
                role="tab"
                aria-selected={view === "pulse"}
                onClick={() => setView("pulse")}
                className={`px-3 py-1.5 text-sm transition-colors ${
                  view === "pulse"
                    ? "bg-primary text-white"
                    : "bg-surface text-foreground hover:bg-surface-hover"
                }`}
              >
                Pulse
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={view === "strategy"}
                onClick={() => setView("strategy")}
                className={`px-3 py-1.5 text-sm transition-colors border-l border-border ${
                  view === "strategy"
                    ? "bg-primary text-white"
                    : "bg-surface text-foreground hover:bg-surface-hover"
                }`}
              >
                Strategy
              </button>
            </div>
          </div>
        </div>

        {/* Customer scope — a dropdown ("my customers" or a named
            colleague's, for covering them while they're off) plus a
            separate "Show all customers" toggle. Kept as two distinct
            controls rather than one combined dropdown-with-an-"all"-entry:
            "pick a person" and "see everyone" are different kinds of
            choice (one narrows to an individual, the other removes
            scoping entirely), and folding them into one list would bury
            "All customers" as just another row among a growing colleague
            roster. See docs/features/customer-list-colleague-filter/. */}
        {canToggleScope && (
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <label className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Customers:</span>
              <select
                aria-label="Customer scope"
                value={showAll ? "" : viewedUserId}
                disabled={showAll}
                onChange={(e) => {
                  setViewedUserId(e.target.value);
                  if (e.target.value) setShowAll(false);
                }}
                className="px-3 py-1.5 border border-border rounded bg-background text-sm disabled:opacity-50"
              >
                <option value="">My customers</option>
                {colleagues.map((colleague) => (
                  <option key={colleague.id} value={colleague.id}>
                    {colleague.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              aria-pressed={showAll}
              onClick={() => {
                const next = !showAll;
                setShowAll(next);
                if (next) setViewedUserId("");
              }}
              className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                showAll
                  ? "bg-primary text-white border-primary"
                  : "bg-surface border-border text-foreground hover:bg-surface-hover"
              }`}
            >
              Show all customers
            </button>
          </div>
        )}

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          {/* Search — hybrid typeahead: type to filter the list, or pick a
              customer suggestion to filter the list down to that customer. */}
          <SearchTypeahead
            value={searchQuery}
            onValueChange={setSearchQuery}
            placeholder="Search customers..."
            ariaLabel="Search customers"
            fetchSuggestions={fetchSuggestions}
            onSelectSuggestion={(s) => setSearchQuery(s.label)}
            className="flex-1 max-w-md"
          />

          {/* Segment Filter Pills */}
          <div className="flex gap-2 flex-wrap">
            {segments.map((segment) => (
              <button
                key={segment.id}
                onClick={() => setSegmentFilter(segment.id)}
                className={`
                  px-4 py-2 rounded-full text-sm transition-colors
                  ${
                    segmentFilter === segment.id
                      ? "bg-primary text-white"
                      : "bg-surface border border-border text-foreground hover:bg-surface-hover"
                  }
                `}
              >
                {segment.label} ({formatCount(segment.count)})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Strategy-only filter rows. Multi-select within a category,
          AND across. Hidden in pulse view. */}
      {view === "strategy" && (
        <div className="space-y-3">
          <StrategyFilterRow
            label="Opportunity"
            keys={UNTAPPED_TIER_KEYS}
            labels={UNTAPPED_TIER_LABEL}
            selected={new Set(untappedTierFilters)}
            onToggle={(v) => toggleStrategyFilter("untapped_tier", v)}
          />
          <StrategyFilterRow
            label="Relationship depth"
            keys={COVERAGE_KEYS}
            labels={COVERAGE_LABEL}
            selected={new Set(coverageFilters)}
            onToggle={(v) => toggleStrategyFilter("coverage", v)}
          />
          <StrategyFilterRow
            label="Account health"
            keys={HEALTH_TIER_KEYS}
            labels={HEALTH_TIER_LABEL}
            selected={new Set(healthTierFilters)}
            onToggle={(v) => toggleStrategyFilter("health_tier", v)}
          />
        </div>
      )}

      {/* Results Count */}
      <div className="text-sm text-muted-foreground">
        Showing {formatCount((page - 1) * PAGE_SIZE + 1)}–
        {formatCount(Math.min(page * PAGE_SIZE, total))} of {formatCount(total)}{" "}
        customers
      </div>

      {/* Sort and Filter Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Sort */}
        <div className="flex items-center gap-2">
          <ArrowUpDown className="size-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Sort by:</span>
          {view === "pulse" ? (
            <select
              value={pulseSort}
              onChange={(e) => setPulseSort(e.target.value as PulseSortOption)}
              className="px-3 py-1.5 border border-border rounded bg-background text-sm"
            >
              <option value="revenue_12mo">Revenue (12 mo)</option>
              <option value="revenue_90d">Revenue (90 days)</option>
              <option value="estimates_total">Live Estimates</option>
              <option value="jobs_total">Live Jobs</option>
              <option value="last_estimate">Last Estimate Date</option>
              <option value="last_job">Last Job Date</option>
              <option value="alerts_count">Alerts Count</option>
              <option value="tickets_count">Tickets Count</option>
            </select>
          ) : (
            <select
              value={strategySort}
              onChange={(e) =>
                setStrategySort(e.target.value as StrategySortOption)
              }
              className="px-3 py-1.5 border border-border rounded bg-background text-sm"
            >
              <option value="health_score_asc">
                Health score (worst first)
              </option>
              <option value="untapped_gbp_desc">Untapped £ (high first)</option>
              <option value="wallet_gbp_desc">Wallet £ (high first)</option>
              <option value="strategic_key_contacts_asc">
                Strategic + key contacts (fewest first)
              </option>
              <option value="name">Name</option>
            </select>
          )}
        </div>
      </div>

      {/* Customer List */}
      {customers.length === 0 ? (
        <div className="text-muted-foreground text-sm p-8 text-center border border-border rounded-lg">
          No customers found
        </div>
      ) : (
        <div className="space-y-4">
          {customers.map((customer) =>
            view === "strategy" ? (
              <CustomerStrategyRowCard
                key={customer.id}
                customer={customer}
                href={paths.customerDetail({ customerId: customer.id })}
                onAddTicket={(customerId) =>
                  setTicketModalCustomerId(customerId)
                }
              />
            ) : (
              <CustomerCard
                key={customer.id}
                customer={customer}
                href={paths.customerDetail({ customerId: customer.id })}
                onAddTicket={(customerId) =>
                  setTicketModalCustomerId(customerId)
                }
              />
            ),
          )}
        </div>
      )}

      <ListPagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />

      {ticketModalCustomerId && (
        <TicketModal
          onClose={() => setTicketModalCustomerId(null)}
          onSave={handleAddTicket}
          isSubmitting={isSubmittingTicket}
        />
      )}
    </div>
  );
}

interface StrategyFilterRowProps<K extends string> {
  label: string;
  keys: ReadonlyArray<K>;
  labels: Record<K, string>;
  selected: Set<string>;
  onToggle: (value: K) => void;
}

function StrategyFilterRow<K extends string>({
  label,
  keys,
  labels,
  selected,
  onToggle,
}: StrategyFilterRowProps<K>) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
      <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium sm:w-44 shrink-0">
        {label}
      </div>
      <div className="flex flex-wrap gap-2">
        {keys.map((k) => {
          const isActive = selected.has(k);
          return (
            <button
              key={k}
              type="button"
              onClick={() => onToggle(k)}
              aria-pressed={isActive}
              className={`px-3 py-1 rounded-full text-xs transition-colors border ${
                isActive
                  ? "bg-primary text-white border-primary"
                  : "bg-surface border-border text-foreground hover:bg-surface-hover"
              }`}
            >
              {labels[k]}
            </button>
          );
        })}
      </div>
    </div>
  );
}
